import { useState, useEffect, useMemo } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth, API, hasPermission } from "@/App";
import {
  LayoutDashboard, ShoppingCart, Package, Receipt, Settings, LogOut, Store,
  ChevronLeft, ChevronDown, Boxes, Menu, PackageX, Truck, Route, Smartphone,
  Users, FileText, AlertCircle, Eye, ListTodo, Share2, Megaphone, MapPin,
  PhoneCall, CalendarClock, MessageSquare, Gift, Trash2, Car, Sparkles, PackagePlus, ShieldCheck,
  Move, Check as CheckIcon, LayoutGrid, BarChart3, Building2, Search, User,
} from "lucide-react";
import GlobalSearch from "@/components/GlobalSearch";
import { SortableList, SortableItem, applySavedOrder, saveOrder } from "@/components/SortableWrapper";
import { useBadges } from "@/hooks/useBadges";
import axios from "axios";

const navItems = [
  { path: "/", label: "Accueil", icon: LayoutGrid, permission: "dashboard" },
  { path: "/statistiques", label: "Statistiques", icon: BarChart3, permission: "dashboard" },
  { path: "/ventes", label: "Ventes", icon: ShoppingCart, permission: "ventes" },
  { path: "/vente-depot", label: "Vente sur place", icon: Store, permission: "ventes" },
  { path: "/stock", label: "Stock", icon: Package, permission: "stock" },
  { path: "/produits", label: "Produits", icon: Boxes, permission: "produits" },
  { path: "/pertes", label: "Pertes", icon: PackageX, permission: "pertes" },
  { path: "/livraisons", label: "Livraisons", icon: Truck, permission: "livraisons" },
  { path: "/tournees", label: "Tournées", icon: Route, permission: "tournees" },
  { path: "/zones", label: "Carte des ventes", icon: MapPin, permission: "zones" },
  { path: "/charges", label: "Charges", icon: Receipt, permission: "charges" },
  { path: "/clients", label: "Clients", icon: Users, permission: "clients" },
  { path: "/rappels", label: "À rappeler", icon: PhoneCall, permission: "rappels" },
  { path: "/rendez-vous", label: "Rendez-vous", icon: CalendarClock, permission: "rdv" },
  { path: "/devis", label: "Devis & Factures", icon: FileText, permission: "devis" },
  { path: "/impayes", label: "Impayés", icon: AlertCircle, permission: "impayes" },
  { path: "/veille", label: "Veille", icon: Eye, permission: "veille" },
  { path: "/taches", label: "Tâches", icon: ListTodo, permission: "taches" },
  { path: "/social", label: "Réseaux Sociaux", icon: Share2, permission: "social" },
  { path: "/marketing", label: "Marketing", icon: Megaphone, permission: "marketing" },
  { path: "/communications", label: "Communications", icon: MessageSquare, badgeKey: "communications", permission: "communications" },
  { path: "/parrainages", label: "Parrainages", icon: Gift, permission: "parrainages" },
  { path: "/corbeille", label: "Corbeille", icon: Trash2, permission: "corbeille" },
  { path: "/audit", label: "Journal d'audit", icon: ShieldCheck, permission: "parametres" },
  { path: "/vehicules", label: "Véhicules", icon: Car, permission: "vehicules" },
  { path: "/arrivages", label: "Arrivages", icon: PackagePlus, permission: "arrivages" },
  { path: "/parametres", label: "Paramètres", icon: Settings, permission: "parametres" },
  { path: "/livreur", label: "Mode Livreur", icon: Smartphone, special: true, permission: "livreur" },
  { path: "/assistant-ia", label: "Assistant IA", icon: Sparkles, special: true, permission: "ai_chef" },
  { path: "/b2b-admin", label: "Portail B2B", icon: Building2, permission: "parametres" },
];

// Catégories du drawer mobile — chaque path listé DOIT exister dans navItems.
// Tout module non catégorisé sera placé automatiquement dans "Autres".
const CATEGORIES = [
  { label: "Ventes & Clients", paths: ["/vente-depot", "/clients", "/rappels", "/rendez-vous", "/devis", "/impayes"] },
  { label: "Logistique",        paths: ["/livraisons", "/tournees", "/zones", "/arrivages", "/vehicules", "/livreur"] },
  { label: "Gestion",           paths: ["/produits", "/stock", "/statistiques", "/charges", "/pertes", "/veille", "/taches"] },
  { label: "Marketing",         paths: ["/social", "/marketing", "/communications", "/parrainages"] },
  { label: "Outils",            paths: ["/assistant-ia", "/b2b-admin", "/corbeille", "/parametres", "/audit"] },
];

const Sidebar = ({ isOpen, onToggle }) => {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const badges = useBadges();
  const [branding, setBranding] = useState({ logo_url: "", company_name: "TopCocoon" });
  const [editMode, setEditMode] = useState(false);
  const [orderedItems, setOrderedItems] = useState(() => applySavedOrder(navItems, "path", "sidebar_order"));
  // Mobile drawer — état recherche + catégories dépliées + mini menu profil
  const [searchQ, setSearchQ] = useState("");
  const [openCats, setOpenCats] = useState(() => new Set(["Ventes & Clients", "Logistique"]));
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  // Desktop : catégorie ouverte (une seule à la fois — accordéon Odoo-style)
  const [openCatDesktop, setOpenCatDesktop] = useState("Ventes & Clients");
  // Favoris desktop = Accueil + modules prioritaires
  const DESKTOP_FAV_PATHS = ["/", "/ventes", "/livraisons", "/stock", "/clients", "/statistiques", "/b2b-admin", "/produits"];

  const handleReorder = (newOrderPaths) => {
    const map = new Map(orderedItems.map(i => [i.path, i]));
    const reordered = newOrderPaths.map(p => map.get(p)).filter(Boolean);
    setOrderedItems(reordered);
    saveOrder("sidebar_order", newOrderPaths);
  };

  const resetOrder = () => {
    localStorage.removeItem("sidebar_order");
    setOrderedItems(navItems);
    setEditMode(false);
  };

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const response = await axios.get(`${API}/branding`);
        setBranding(response.data);
      } catch { /* silent */ }
    };
    fetchBranding();
  }, []);

  const handleLogout = () => { logout(); navigate("/login"); };

  const visibleItems = useMemo(
    () => orderedItems.filter((item) => hasPermission(user, item.permission)),
    [orderedItems, user]
  );

  // Résultats recherche mobile — filtre côté client sur label
  const searchResults = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    if (!q) return null;
    return visibleItems.filter((it) => it.label.toLowerCase().includes(q));
  }, [searchQ, visibleItems]);

  // Catégorisation mobile — items groupés + "Autres" pour ceux non listés
  const categorized = useMemo(() => {
    const byPath = new Map(visibleItems.map((it) => [it.path, it]));
    const used = new Set();
    const groups = CATEGORIES.map((cat) => ({
      label: cat.label,
      items: cat.paths.map((p) => { used.add(p); return byPath.get(p); }).filter(Boolean),
    })).filter((g) => g.items.length > 0);
    const others = visibleItems.filter((it) => !used.has(it.path) && it.path !== "/" && it.path !== "/ventes" && it.path !== "/livraisons" && it.path !== "/stock");
    if (others.length > 0) groups.push({ label: "Autres", items: others });
    return groups;
  }, [visibleItems]);

  const toggleCat = (label) => {
    setOpenCats((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label); else next.add(label);
      return next;
    });
  };

  return (
    <>
      {/* Bouton hamburger — masqué sur mobile (BottomNav "Plus" prend le relais), affiché uniquement sur tablette non-lg */}
      <button
        onClick={onToggle}
        className="fixed left-4 z-50 hidden md:block lg:hidden p-2 rounded-lg bg-white shadow-md"
        style={{ top: "calc(env(safe-area-inset-top, 0px) + 16px)" }}
        data-testid="mobile-menu-btn"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Overlay */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={onToggle} />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 h-full w-72 bg-white border-r border-slate-200 z-40 transform transition-transform duration-300 ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0 lg:w-64`}
        data-testid="sidebar"
      >
        {/* Logo — clic = retour à l'accueil */}
        <div className="h-16 flex items-center justify-between px-4 lg:px-6 border-b border-slate-200">
          <NavLink to="/" end className="flex items-center gap-3 min-w-0 hover:opacity-80 transition" data-testid="sidebar-home-link">
            <div className="w-11 h-11 bg-white rounded-xl flex items-center justify-center shadow-sm border border-slate-200 overflow-hidden p-1 flex-shrink-0">
              <img
                src={branding.logo_url || "/logo-topcocoon.png"}
                alt={branding.company_name || "TopCocoon"}
                className="w-full h-full object-contain"
                data-testid="sidebar-logo"
                onError={(e) => { e.currentTarget.src = "/logo-topcocoon.png"; }}
              />
            </div>
            <span className="font-semibold text-lg text-slate-800 truncate">{branding.company_name || "TopCocoon"}</span>
          </NavLink>
          <button
            onClick={onToggle}
            className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors lg:hidden"
            data-testid="close-sidebar-btn"
          >
            <ChevronLeft className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <nav className="py-3 flex flex-col h-[calc(100%-4rem)]">
          {/* === MOBILE / TABLET DRAWER === */}
          <div className="lg:hidden flex-1 overflow-y-auto px-3 space-y-2" style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}>
            {/* Recherche locale des modules */}
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                placeholder="Rechercher un module…"
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                data-testid="mobile-menu-search"
              />
            </div>

            {searchResults ? (
              <div className="space-y-1 pt-1" data-testid="mobile-menu-search-results">
                {searchResults.length === 0 ? (
                  <p className="text-xs text-slate-400 italic py-3 text-center">Aucun module trouvé</p>
                ) : searchResults.map((item) => (
                  <MobileNavLink key={item.path} item={item} badges={badges} onClick={onToggle} />
                ))}
              </div>
            ) : (
              <>
                {categorized.map((group) => {
                  const opened = openCats.has(group.label);
                  return (
                    <div key={group.label} className="border-b border-slate-100 pb-1">
                      <button
                        type="button"
                        onClick={() => toggleCat(group.label)}
                        className="w-full flex items-center justify-between px-2 py-2 text-[11px] font-bold text-slate-500 uppercase tracking-wide hover:text-slate-700"
                        data-testid={`mobile-menu-cat-${group.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                      >
                        <span>{group.label}</span>
                        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${opened ? "rotate-180" : ""}`} />
                      </button>
                      {opened && (
                        <div className="space-y-0.5 pl-1">
                          {group.items.map((item) => (
                            <MobileNavLink key={item.path} item={item} badges={badges} onClick={onToggle} />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>

          {/* === DESKTOP LIST — Odoo-style : Favoris + catégories accordéon === */}
          <div className="hidden lg:flex flex-col flex-1 min-h-0">
            <div className="flex-1 overflow-y-auto px-3 space-y-1">
              <div className="mb-2"><GlobalSearch /></div>
              <div className="mb-2 flex gap-1">
                <button
                  type="button"
                  onClick={() => setEditMode(!editMode)}
                  className={`flex-1 text-xs px-2 py-1.5 rounded-lg transition-all ${editMode ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                  data-testid="sidebar-edit-toggle"
                  title="Réorganiser le menu"
                >
                  {editMode ? (<><CheckIcon className="w-3 h-3 inline mr-1" />Terminé</>) : (<><Move className="w-3 h-3 inline mr-1" />Réorganiser</>)}
                </button>
                {editMode && (
                  <button type="button" onClick={resetOrder} className="text-xs px-2 py-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100" data-testid="sidebar-reset-order" title="Restaurer l'ordre par défaut">Reset</button>
                )}
              </div>

              {/* Mode Réorganisation : conserve la liste plate draggable existante */}
              {editMode ? (() => {
                const ids = visibleItems.map(i => i.path);
                return (
                  <SortableList items={ids} onReorder={handleReorder}>
                    {visibleItems.map((item) => (
                      <SortableItem key={item.path} id={item.path} editMode={editMode}>
                        <div
                          className={`flex items-center gap-3 px-4 py-3 rounded-lg pointer-events-none opacity-90 pr-10 ${item.special ? "bg-green-50 text-green-700 border border-green-100" : "text-slate-600 bg-slate-50"}`}
                          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <item.icon className="w-5 h-5" />
                          <span className="flex-1">{item.label}</span>
                        </div>
                      </SortableItem>
                    ))}
                  </SortableList>
                );
              })() : (
                <>
                  {/* Favoris — visible en permanence en haut */}
                  <p className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                    <span className="text-amber-400">★</span> Favoris
                  </p>
                  <div className="space-y-0.5">
                    {DESKTOP_FAV_PATHS
                      .map((p) => visibleItems.find((i) => i.path === p))
                      .filter(Boolean)
                      .slice(0, 6)
                      .map((item) => (
                        <NavLink
                          key={`fav-${item.path}`}
                          to={item.path}
                          end={item.path === "/"}
                          className={({ isActive }) =>
                            `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition ${
                              item.special
                                ? isActive ? "bg-green-100 text-green-700 font-medium" : "text-green-700 hover:bg-green-50"
                                : isActive
                                  ? "bg-indigo-50 text-indigo-700 font-medium"
                                  : "text-slate-700 hover:bg-slate-100"
                            }`
                          }
                          data-testid={`nav-fav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <item.icon className="w-4 h-4" />
                          <span className="flex-1">{item.label}</span>
                          {item.badgeKey && badges[item.badgeKey] > 0 && (
                            <span className="w-2 h-2 rounded-full bg-indigo-500" aria-hidden />
                          )}
                        </NavLink>
                      ))}
                  </div>

                  {/* Catégories — accordéon (une seule ouverte à la fois) */}
                  <div className="pt-3 space-y-1">
                    {categorized.map((cat) => {
                      const catBadge = cat.items.reduce((s, it) => s + (it.badgeKey ? (badges[it.badgeKey] || 0) : 0), 0);
                      const opened = openCatDesktop === cat.label;
                      return (
                        <div key={cat.label}>
                          <button
                            type="button"
                            onClick={() => setOpenCatDesktop(opened ? "" : cat.label)}
                            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-[11px] font-bold uppercase tracking-wider transition ${opened ? "text-indigo-700 bg-indigo-50/50" : "text-slate-500 hover:bg-slate-50"}`}
                            data-testid={`sidebar-cat-toggle-${cat.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
                          >
                            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${opened ? "" : "-rotate-90"}`} />
                            <span className="flex-1 text-left">{cat.label}</span>
                            {catBadge > 0 && (
                              <span className="bg-indigo-100 text-indigo-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                                {catBadge > 99 ? "99+" : catBadge}
                              </span>
                            )}
                          </button>
                          {opened && (
                            <div className="pl-3 mt-1 space-y-0.5">
                              {cat.items.map((item) => (
                                <NavLink
                                  key={item.path}
                                  to={item.path}
                                  end={item.path === "/"}
                                  className={({ isActive }) =>
                                    `flex items-center gap-2.5 pl-4 pr-3 py-1.5 rounded-lg text-sm transition ${
                                      item.special
                                        ? isActive ? "bg-green-100 text-green-700 font-medium" : "text-green-700 hover:bg-green-50"
                                        : isActive
                                          ? "bg-indigo-50 text-indigo-700 font-medium"
                                          : "text-slate-600 hover:bg-slate-100"
                                    }`
                                  }
                                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                                >
                                  <item.icon className="w-4 h-4" />
                                  <span className="flex-1">{item.label}</span>
                                  {item.badgeKey && badges[item.badgeKey] > 0 && (
                                    <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center" data-testid={`sidebar-badge-${item.badgeKey}`}>
                                      {badges[item.badgeKey]}
                                    </span>
                                  )}
                                </NavLink>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {/* Profil bas — avatar + nom + email + menu déconnexion */}
            <div className="px-3 pt-3 border-t border-slate-200 mt-auto pb-3 relative">
              {profileMenuOpen && (
                <div className="absolute bottom-full left-3 right-3 mb-1 bg-white border rounded-xl shadow-xl overflow-hidden z-10" data-testid="desktop-profile-menu">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 text-left"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4" /> Déconnexion
                  </button>
                </div>
              )}
              <button
                type="button"
                onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                className="w-full flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-slate-50 transition"
                data-testid="desktop-profile-toggle"
              >
                <div className="w-9 h-9 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-semibold text-sm flex-shrink-0">
                  {(user?.name || user?.email || "A")[0].toUpperCase()}
                </div>
                <div className="min-w-0 flex-1 text-left">
                  <p className="text-sm font-medium text-slate-800 truncate">{user?.name || "Admin TopCocoon"}</p>
                  <p className="text-[10px] text-slate-500 truncate">{user?.email}</p>
                </div>
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${profileMenuOpen ? "rotate-180" : ""}`} />
              </button>
            </div>
          </div>

          {/* === MOBILE : bloc profil compact en bas === */}
          <div className="lg:hidden border-t border-slate-200 relative" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
            {profileMenuOpen && (
              <div className="absolute bottom-full left-2 right-2 mb-1 bg-white border rounded-xl shadow-xl overflow-hidden" data-testid="mobile-profile-menu">
                <button
                  onClick={() => { setProfileMenuOpen(false); onToggle(); navigate("/parametres"); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 text-left"
                  data-testid="mobile-profile-account"
                >
                  <User className="w-4 h-4" /> Mon compte
                </button>
                <button
                  onClick={() => { setProfileMenuOpen(false); onToggle(); navigate("/parametres"); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 text-left border-t"
                  data-testid="mobile-profile-settings"
                >
                  <Settings className="w-4 h-4" /> Paramètres
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 text-left border-t"
                  data-testid="mobile-profile-logout"
                >
                  <LogOut className="w-4 h-4" /> Déconnexion
                </button>
              </div>
            )}
            <button
              type="button"
              onClick={() => setProfileMenuOpen(!profileMenuOpen)}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50"
              data-testid="mobile-profile-toggle"
            >
              <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-semibold text-sm flex-shrink-0">
                {(user?.name || user?.email || "A")[0].toUpperCase()}
              </div>
              <div className="min-w-0 flex-1 text-left">
                <p className="text-sm font-medium text-slate-800 truncate">{user?.name || "Admin TopCocoon"}</p>
                <p className="text-[10px] text-slate-400 truncate">{user?.email}</p>
              </div>
              <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${profileMenuOpen ? "rotate-180" : ""}`} />
            </button>
          </div>
        </nav>
      </aside>
    </>
  );
};

// Sous-composant local — item de menu mobile
const MobileNavLink = ({ item, badges, onClick }) => (
  <NavLink
    to={item.path}
    end={item.path === "/"}
    onClick={onClick}
    className={({ isActive }) =>
      `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition ${
        item.special
          ? isActive
            ? "bg-green-100 text-green-700 font-medium"
            : "text-green-700 bg-green-50 hover:bg-green-100"
          : isActive
            ? "bg-indigo-50 text-indigo-600 font-medium"
            : "text-slate-700 hover:bg-slate-100"
      }`
    }
    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <item.icon className="w-4.5 h-4.5" />
    <span className="flex-1">{item.label}</span>
    {item.badgeKey && badges[item.badgeKey] > 0 && (
      <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
        {badges[item.badgeKey]}
      </span>
    )}
  </NavLink>
);

export default Sidebar;
